<template>
    <div class="flex flex-col dark:text-white">
        <!-- Cards de Alertas -->
        <div class="mt-6 mb-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div class="rounded-lg bg-slate-50 dark:bg-slate-800/25 p-4">
                <div class="flex items-center justify-between">
                    <div class="flex items-center space-x-2">
                        <font-awesome-icon :icon="['fas', 'triangle-exclamation']" class="h-5 w-5 text-red-500" />
                        <span class="font-semibold text-red-500">Alertas Críticos</span>
                    </div>
                    <div class="bg-red-500 text-white text-xs px-2 py-1 rounded-full">{{ dashboardData.critical_alerts || 0 }}</div>
                </div>
                <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">Requer ação imediata</p>
            </div>

            <div class="rounded-lg bg-slate-50 dark:bg-slate-800/25 p-4">
                <div class="flex items-center justify-between">
                    <div class="flex items-center space-x-2">
                        <font-awesome-icon :icon="['fas', 'flag']" class="h-5 w-5 text-orange-500" />
                        <span class="font-semibold text-orange-500">Em Investigação</span>
                    </div>
                    <div class="bg-orange-500 text-white text-xs px-2 py-1 rounded-full">{{ dashboardData.under_investigation || 0 }}</div>
                </div>
                <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">Análise em andamento</p>
            </div>

            <div class="rounded-lg bg-slate-50 dark:bg-slate-800/25 p-4">
                <div class="flex items-center justify-between">
                    <div class="flex items-center space-x-2">
                        <font-awesome-icon :icon="['fas', 'check-circle']" class="h-5 w-5 text-green-500" />
                        <span class="font-semibold text-green-500">Taxa de Detecção</span>
                    </div>
                    <div class="bg-green-500 text-white text-xs px-2 py-1 rounded-full">{{ dashboardData.detection_rate || 0 }}%</div>
                </div>
                <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">Eficácia do sistema</p>
            </div>
        </div>

        <!-- Cards de Métricas da Plataforma -->
        <div class="mb-6">
            <h2 class="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Métricas Gerais da Plataforma (Últimos 30 dias)</h2>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <!-- Índice de Chargeback (Competência) -->
                <div class="rounded-lg bg-slate-50 dark:bg-slate-800/25 p-4">
                    <div class="flex items-center justify-between mb-2">
                        <div class="flex items-center space-x-2">
                            <font-awesome-icon :icon="['fas', 'chart-line']" class="h-5 w-5 text-red-600" />
                            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Índice de Chargeback (Competência)</span>
                        </div>
                    </div>
                    <div class="text-2xl font-bold text-red-600">{{ formatPercentage(platformMetrics.chargeback_index_competence) }}%</div>
                    <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">% real de vendas que viraram chargeback</p>
                </div>

                <!-- Índice de Reembolso (Competência) -->
                <div class="rounded-lg bg-slate-50 dark:bg-slate-800/25 p-4">
                    <div class="flex items-center justify-between mb-2">
                        <div class="flex items-center space-x-2">
                            <font-awesome-icon :icon="['fas', 'rotate-left']" class="h-5 w-5 text-orange-600" />
                            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Índice de Reembolso (Competência)</span>
                        </div>
                    </div>
                    <div class="text-2xl font-bold text-orange-600">{{ formatPercentage(platformMetrics.refund_index_competence) }}%</div>
                    <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Nível de satisfação geral dos clientes</p>
                </div>

                <!-- Volume Total de Chargeback -->
                <div class="rounded-lg bg-slate-50 dark:bg-slate-800/25 p-4">
                    <div class="flex items-center justify-between mb-2">
                        <div class="flex items-center space-x-2">
                            <font-awesome-icon :icon="['fas', 'money-bill-wave']" class="h-5 w-5 text-red-500" />
                            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Volume Total de Chargeback</span>
                        </div>
                    </div>
                    <div class="text-2xl font-bold text-red-500">{{ formatCurrency(platformMetrics.total_chargeback_volume) }}</div>
                    <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Impacto financeiro direto dos chargebacks</p>
                </div>

                <!-- Volume Total de Reembolso -->
                <div class="rounded-lg bg-slate-50 dark:bg-slate-800/25 p-4">
                    <div class="flex items-center justify-between mb-2">
                        <div class="flex items-center space-x-2">
                            <font-awesome-icon :icon="['fas', 'hand-holding-dollar']" class="h-5 w-5 text-yellow-600" />
                            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Volume Total de Reembolso</span>
                        </div>
                    </div>
                    <div class="text-2xl font-bold text-yellow-600">{{ formatCurrency(platformMetrics.total_refund_volume) }}</div>
                    <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Total reembolsado aos clientes</p>
                </div>

                <!-- TPV - Volume Total Processado -->
                <div class="rounded-lg bg-slate-50 dark:bg-slate-800/25 p-4">
                    <div class="flex items-center justify-between mb-2">
                        <div class="flex items-center space-x-2">
                            <font-awesome-icon :icon="['fas', 'chart-bar']" class="h-5 w-5 text-blue-600" />
                            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">TPV - Volume Total Processado</span>
                        </div>
                    </div>
                    <div class="text-2xl font-bold text-blue-600">{{ formatCurrency(platformMetrics.tpv) }}</div>
                    <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Total transacionado na plataforma</p>
                </div>

                <!-- TPN - Total de Transações -->
                <div class="rounded-lg bg-slate-50 dark:bg-slate-800/25 p-4">
                    <div class="flex items-center justify-between mb-2">
                        <div class="flex items-center space-x-2">
                            <font-awesome-icon :icon="['fas', 'list-ol']" class="h-5 w-5 text-indigo-600" />
                            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">TPN - Total de Transações</span>
                        </div>
                    </div>
                    <div class="text-2xl font-bold text-indigo-600">{{ formatNumber(platformMetrics.tpn) }}</div>
                    <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">Quantidade total de transações</p>
                </div>
            </div>
        </div>

        <!-- Métricas de Tendência e Variação -->
        <div class="mb-6">
            <h2 class="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Métricas de Tendência</h2>
            <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
                <!-- Variação do Índice de Chargeback -->
                <div class="rounded-lg bg-slate-50 dark:bg-slate-800/25 p-4">
                    <div class="flex items-center justify-between mb-2">
                        <div class="flex items-center space-x-2">
                            <font-awesome-icon :icon="['fas', 'chart-line']" class="h-5 w-5 text-purple-600" />
                            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Variação Índice Chargeback</span>
                        </div>
                    </div>
                    <div class="flex items-center space-x-2">
                        <span class="text-2xl font-bold" :class="trendMetrics.chargeback_index_variation > 0 ? 'text-red-600' : 'text-green-600'">
                            {{ trendMetrics.chargeback_index_variation > 0 ? '+' : '' }}{{ trendMetrics.chargeback_index_variation }}%
                        </span>
                        <font-awesome-icon 
                            :icon="['fas', trendMetrics.chargeback_index_variation > 0 ? 'arrow-up' : 'arrow-down']" 
                            :class="trendMetrics.chargeback_index_variation > 0 ? 'text-red-500' : 'text-green-500'"
                        />
                    </div>
                    <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {{ trendMetrics.current_period_start }}-{{ trendMetrics.current_period_end }} vs 
                        {{ trendMetrics.previous_period_start }}-{{ trendMetrics.previous_period_end }}
                    </p>
                </div>

                <!-- Variação do Volume de Chargeback -->
                <div class="rounded-lg bg-slate-50 dark:bg-slate-800/25 p-4">
                    <div class="flex items-center justify-between mb-2">
                        <div class="flex items-center space-x-2">
                            <font-awesome-icon :icon="['fas', 'money-bill-trend-up']" class="h-5 w-5 text-purple-600" />
                            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Variação Volume Chargeback</span>
                        </div>
                    </div>
                    <div class="flex items-center space-x-2">
                        <span class="text-2xl font-bold" :class="trendMetrics.chargeback_volume_variation > 0 ? 'text-red-600' : 'text-green-600'">
                            {{ trendMetrics.chargeback_volume_variation > 0 ? '+' : '' }}{{ trendMetrics.chargeback_volume_variation }}%
                        </span>
                        <font-awesome-icon 
                            :icon="['fas', trendMetrics.chargeback_volume_variation > 0 ? 'arrow-up' : 'arrow-down']" 
                            :class="trendMetrics.chargeback_volume_variation > 0 ? 'text-red-500' : 'text-green-500'"
                        />
                    </div>
                    <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {{ formatCurrency(trendMetrics.chargeback_volume_diff) }} | 
                        {{ trendMetrics.current_period_start }}-{{ trendMetrics.current_period_end }} vs 
                        {{ trendMetrics.previous_period_start }}-{{ trendMetrics.previous_period_end }}
                    </p>
                </div>

                <!-- Novos Vendedores com Alerta de Risco -->
                <div class="rounded-lg bg-slate-50 dark:bg-slate-800/25 p-4">
                    <div class="flex items-center justify-between mb-2">
                        <div class="flex items-center space-x-2">
                            <font-awesome-icon :icon="['fas', 'user-shield']" class="h-5 w-5 text-orange-600" />
                            <span class="text-sm font-medium text-gray-700 dark:text-gray-300">Novos Vendedores em Risco</span>
                        </div>
                    </div>
                    <div class="text-2xl font-bold text-orange-600">{{ trendMetrics.new_risky_sellers }}</div>
                    <p class="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {{ trendMetrics.current_period_start }}-{{ trendMetrics.current_period_end }} com score ≥ 40
                    </p>
                </div>
            </div>
        </div>

        <!-- Métricas de Risco e Ação -->
        <div class="mb-6">
            <h2 class="text-lg font-semibold mb-4 text-gray-900 dark:text-white">Métricas de Risco e Ação</h2>
            
            <!-- Distribuição de Risco -->
            <div class="mb-4 rounded-lg bg-slate-50 dark:bg-slate-800/25 p-4">
                <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Distribuição de Risco dos Vendedores</h3>
                <div class="space-y-2">
                    <div class="flex items-center justify-between">
                        <span class="text-sm text-gray-600 dark:text-gray-400">🟢 Baixo (0-39)</span>
                        <div class="flex items-center space-x-2">
                            <div class="w-32 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                <div class="bg-green-500 h-2 rounded-full" :style="`width: ${riskMetrics.risk_distribution.low_percentage}%`"></div>
                            </div>
                            <span class="text-sm font-medium text-gray-900 dark:text-white">{{ riskMetrics.risk_distribution.low_percentage }}%</span>
                        </div>
                    </div>
                    <div class="flex items-center justify-between">
                        <span class="text-sm text-gray-600 dark:text-gray-400">🟡 Médio (40-79)</span>
                        <div class="flex items-center space-x-2">
                            <div class="w-32 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                <div class="bg-yellow-500 h-2 rounded-full" :style="`width: ${riskMetrics.risk_distribution.medium_percentage}%`"></div>
                            </div>
                            <span class="text-sm font-medium text-gray-900 dark:text-white">{{ riskMetrics.risk_distribution.medium_percentage }}%</span>
                        </div>
                    </div>
                    <div class="flex items-center justify-between">
                        <span class="text-sm text-gray-600 dark:text-gray-400">🔴 Alto (80+)</span>
                        <div class="flex items-center space-x-2">
                            <div class="w-32 bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                                <div class="bg-red-500 h-2 rounded-full" :style="`width: ${riskMetrics.risk_distribution.high_percentage}%`"></div>
                            </div>
                            <span class="text-sm font-medium text-gray-900 dark:text-white">{{ riskMetrics.risk_distribution.high_percentage }}%</span>
                        </div>
                    </div>
                </div>
            </div>

            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <!-- Top 5 por Índice -->
                <div class="rounded-lg bg-slate-50 dark:bg-slate-800/25 p-4">
                    <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Top 5 - Maior Índice de Chargeback</h3>
                    <div class="space-y-2">
                        <div v-for="(seller, index) in riskMetrics.top_by_index" :key="seller.id" class="flex items-center justify-between">
                            <div class="flex items-center space-x-2">
                                <span class="text-xs font-bold text-gray-500">{{ index + 1 }}.</span>
                                <span class="text-sm text-gray-900 dark:text-white truncate max-w-[200px]">{{ seller.name }}</span>
                            </div>
                            <span class="text-sm font-bold text-red-600">{{ seller.value }}%</span>
                        </div>
                        <div v-if="!riskMetrics.top_by_index || riskMetrics.top_by_index.length === 0" class="text-sm text-gray-500 dark:text-gray-400">
                            Nenhum vendedor com chargeback encontrado
                        </div>
                    </div>
                </div>

                <!-- Top 5 por Volume -->
                <div class="rounded-lg bg-slate-50 dark:bg-slate-800/25 p-4">
                    <h3 class="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">Top 5 - Maior Volume de Chargeback</h3>
                    <div class="space-y-2">
                        <div v-for="(seller, index) in riskMetrics.top_by_volume" :key="seller.id" class="flex items-center justify-between">
                            <div class="flex items-center space-x-2">
                                <span class="text-xs font-bold text-gray-500">{{ index + 1 }}.</span>
                                <span class="text-sm text-gray-900 dark:text-white truncate max-w-[200px]">{{ seller.name }}</span>
                            </div>
                            <span class="text-sm font-bold text-red-600">{{ formatCurrency(seller.value) }}</span>
                        </div>
                        <div v-if="!riskMetrics.top_by_volume || riskMetrics.top_by_volume.length === 0" class="text-sm text-gray-500 dark:text-gray-400">
                            Nenhum vendedor com chargeback encontrado
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <table-component
            :loading="loading"
            :pagination="pagination"
            :headers="headers"
            @fetch="fetch"
        >
            <tr v-for="seller in sellers" :key="seller.id">
                <td class="table-cell-sm">
                    <div>
                        <div class="font-medium text-gray-900 dark:text-white">{{ seller.name }}</div>
                        <div class="text-sm text-gray-500 dark:text-gray-400">{{ seller.email }}</div>
                    </div>
                </td>
                <td class="table-cell-sm">
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium"
                          :class="{
                              'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200': seller.riskScore >= 80,
                              'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200': seller.riskScore >= 40 && seller.riskScore < 80,
                              'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200': seller.riskScore < 40
                          }">
                        {{ seller.riskScore }}%
                    </span>
                </td>
                <td class="table-cell-sm">
                    <span class="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                        Em análise
                    </span>
                </td>
                <td class="table-cell-sm">
                    <div class="flex space-x-2">
                        <button
                            @click="recalculateScore(seller)"
                            :disabled="loading"
                            type="button"
                            :class="{ 'cursor-not-allowed': loading }"
                            class="inline-flex items-center justify-center rounded-md bg-indigo-500 px-2 py-1 text-sm font-semibold leading-6 text-white shadow transition duration-150 ease-in-out hover:bg-indigo-400"
                        >
                            <font-awesome-icon
                                :icon="['fas', 'calculator']"
                                class="mr-1"
                            />
                            Recalcular Score
                        </button>
                    </div>
                </td>
            </tr>
        </table-component>
    </div>
</template>

<script>
import Filters from "../../components/layouts/Filters.vue";
import TableComponent from "../../components/layouts/table/TableComponent.vue";
import axios from 'axios';
import { useToast } from "vue-toastification";

export default {
    components: {
        Filters,
        TableComponent,
    },
    setup() {
        const toast = useToast();
        return { toast };
    },
    data() {
        return {
            loading: false,
            pagination: {
                total: 0,
                per_page: 10,
                current_page: 1,
                last_page: 1,
                from: 1,
                to: 10,
            },
            filter: [],
            sellers: [],
            dashboardData: {},
            platformMetrics: {
                chargeback_index_competence: 0,
                refund_index_competence: 0,
                total_chargeback_volume: 0,
                total_refund_volume: 0,
                tpv: 0,
                tpn: 0
            },
            trendMetrics: {
                chargeback_index_variation: 0,
                chargeback_volume_variation: 0,
                chargeback_volume_diff: 0,
                new_risky_sellers: 0,
                current_period_start: '',
                current_period_end: '',
                previous_period_start: '',
                previous_period_end: ''
            },
            riskMetrics: {
                risk_distribution: {
                    low: 0,
                    medium: 0,
                    high: 0,
                    low_percentage: 0,
                    medium_percentage: 0,
                    high_percentage: 0
                },
                top_by_index: [],
                top_by_volume: []
            }
        }
    },
    computed: {
        headers() {
            return [
                "Vendedor",
                "Score de Risco", 
                "Status",
                "Ações"
            ];
        }
    },
    mounted() {
        this.fetch();
    },
    methods: {
        fetch() {
            this.loading = true;
            fetch('/api/fraud-dashboard')
                .then(response => response.json())
                .then(data => {
                    if (data.data) {
                        this.dashboardData = data.data;
                        this.sellers = data.data.sellers || [];
                        this.platformMetrics = data.data.platform_metrics || this.platformMetrics;
                        this.trendMetrics = data.data.trend_metrics || this.trendMetrics;
                        this.riskMetrics = data.data.risk_metrics || this.riskMetrics;
                        this.pagination.total = data.data.sellers ? data.data.sellers.length : 0;
                    }
                    this.loading = false;
                })
                .catch(error => {
                    console.error('Erro ao carregar dados:', error);
                    this.loading = false;
                });
        },
        getRiskColor(riskScore) {
            if (riskScore >= 80) return 'bg-red-500 text-white';
            if (riskScore >= 40) return 'bg-yellow-500 text-white';
            return 'bg-green-500 text-white';
        },
        getRiskLevel(riskScore) {
            if (riskScore >= 80) return 'Alto Risco';
            if (riskScore >= 40) return 'Médio Risco';
            return 'Baixo Risco';
        },
        getVerificationColor(status) {
            if (status === 'verified') return 'bg-green-500 text-white';
            if (status === 'pending') return 'bg-yellow-500 text-white';
            if (status === 'rejected') return 'bg-red-500 text-white';
            return 'bg-gray-500 text-white';
        },
        getVerificationLabel(status) {
            if (status === 'verified') return 'Verificado';
            if (status === 'pending') return 'Pendente';
            if (status === 'rejected') return 'Rejeitado';
            return 'Não Verificado';
        },
        recalculateScore(seller) {
            this.loading = true;
            
            axios.post(`/api/fraud-dashboard/recalculate/${seller.id}`)
                .then(response => {
                    if (response.data.success) {
                        this.toast.success("Seller foi adicionado a fila para recalcular score");
                        this.fetch(); // Recarregar dados
                    } else {
                        this.toast.error("Não foi possível recalcular score do seller");
                    }
                })
                .catch(error => {
                    console.error('Erro ao recalcular score:', error);
                    this.toast.error("Não foi possível recalcular score do seller");
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        formatCurrency(value) {
            return new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL'
            }).format(value || 0);
        },
        formatPercentage(value) {
            return (value || 0).toFixed(2);
        },
        formatNumber(value) {
            return new Intl.NumberFormat('pt-BR').format(value || 0);
        }
    }
}
</script>